import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  BookOpen, 
  GraduationCap, 
  Camera, 
  FileText, 
  Upload,
  CheckCircle,
  X,
  Gift
} from 'lucide-react';
import { tuitionTeacherService } from '../firebase/tuition';
import { useRealtime } from '../contexts/RealtimeContext';

const TuitionTeacherRegistration = ({ onClose }) => {
  const { currentUser: user } = useRealtime();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    photo: null,
    aadhar: null,
    pan: null
  });
  const [previewUrls, setPreviewUrls] = useState({
    photo: null,
    aadhar: null,
    pan: null
  });
  const [agreementAccepted, setAgreementAccepted] = useState({
    readAndUnderstood: false,
    agreeToTerms: false,
    consentToData: false
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm();

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
    'Hindi', 'Social Studies', 'History', 'Geography', 'Economics',
    'Computer Science', 'Accountancy', 'Business Studies', 'Psychology',
    'Political Science', 'Sociology', 'Philosophy', 'Literature'
  ];

  const classes = [
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
    'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
    'Class 11', 'Class 12', 'JEE Main', 'JEE Advanced', 'NEET',
    'UPSC', 'SSC', 'Banking', 'Railway', 'Defense'
  ];

  const availabilityOptions = [
    { value: 'online', label: 'Online Classes' },
    { value: 'home_visit', label: 'Home Visit' },
    { value: 'student_visit', label: 'Student Comes to Me' }
  ];

  const handleFileUpload = (file, type) => {
    if (file) {
      const url = URL.createObjectURL(file);
      setUploadedFiles(prev => ({ ...prev, [type]: file }));
      setPreviewUrls(prev => ({ ...prev, [type]: url }));
    }
  };

  const removeFile = (type) => {
    setUploadedFiles(prev => ({ ...prev, [type]: null }));
    setPreviewUrls(prev => ({ ...prev, [type]: null }));
  };

  const onSubmit = async (data) => {
    if (!user) {
      toast.error('Please login to register as a teacher');
      return;
    }

    // Check if all agreement checkboxes are accepted
    if (!agreementAccepted.readAndUnderstood || !agreementAccepted.agreeToTerms || !agreementAccepted.consentToData) {
      toast.error('Please accept all terms and conditions to proceed');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload files first
      const fileUrls = {};
      
      if (uploadedFiles.photo) {
        fileUrls.photoUrl = await tuitionTeacherService.uploadTeacherPhoto(
          user.uid, 
          uploadedFiles.photo
        );
      }
      
      if (uploadedFiles.aadhar) {
        fileUrls.aadharUrl = await tuitionTeacherService.uploadTeacherDocument(
          user.uid, 
          uploadedFiles.aadhar, 
          'aadhar'
        );
      }
      
      if (uploadedFiles.pan) {
        fileUrls.panUrl = await tuitionTeacherService.uploadTeacherDocument(
          user.uid, 
          uploadedFiles.pan, 
          'pan'
        );
      }

      // Prepare teacher data
      const referralCode = data.referralCode?.trim().toUpperCase();
      const teacherData = {
        userId: user.uid,
        userEmail: user.email,
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        locality: data.locality,
        city: data.city,
        state: data.state,
        pincode: data.pincode,
        subjects: data.subjects,
        classes: data.classes,
        feesPerHour: parseInt(data.feesPerHour),
        availability: data.availability,
        experience: data.experience,
        qualification: data.qualification,
        bio: data.bio,
        teachingMethod: data.teachingMethod,
        referralCode: referralCode || null, // Store referral code but don't process yet
        referralProcessed: false, // Flag to track if referral was processed
        ...fileUrls
      };

      // Register teacher (referral will be processed when approved)
      const teacherId = await tuitionTeacherService.registerTeacher(teacherData);
      
      if (referralCode) {
        toast.success('Registration submitted successfully! Referral will be processed once your application is approved.');
      } else {
        toast.success('Registration submitted successfully! We will review your application and get back to you soon.');
      }
      
      onClose();
      
    } catch (error) {
      toast.error(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-4">
        <div className="sticky top-0 bg-white border-b px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 pr-2">Register as Tuition Teacher</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1"
            aria-label="Close"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {/* Personal Information */}
          <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <User className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  {...register('phone', { 
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Please enter a valid 10-digit phone number'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience (Years) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('experience', { 
                    required: 'Experience is required',
                    min: { value: 0, message: 'Experience cannot be negative' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Years of teaching experience"
                />
                {errors.experience && (
                  <p className="text-red-500 text-sm mt-1">{errors.experience.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qualification *
              </label>
              <input
                type="text"
                {...register('qualification', { required: 'Qualification is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., B.Tech, M.Sc, Ph.D, etc."
              />
              {errors.qualification && (
                <p className="text-red-500 text-sm mt-1">{errors.qualification.message}</p>
              )}
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <MapPin className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Address Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Address *
                </label>
                <textarea
                  {...register('address', { required: 'Address is required' })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your complete address"
                />
                {errors.address && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Locality *
                  </label>
                  <input
                    type="text"
                    {...register('locality', { required: 'Locality is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Sector 15, Noida"
                  />
                  {errors.locality && (
                    <p className="text-red-500 text-sm mt-1">{errors.locality.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    {...register('city', { required: 'City is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your city"
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    {...register('state', { required: 'State is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your state"
                  />
                  {errors.state && (
                    <p className="text-red-500 text-sm mt-1">{errors.state.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    {...register('pincode', { 
                      required: 'Pincode is required',
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: 'Please enter a valid 6-digit pincode'
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter pincode"
                  />
                  {errors.pincode && (
                    <p className="text-red-500 text-sm mt-1">{errors.pincode.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Information */}
          <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <BookOpen className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Teaching Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subjects You Can Teach *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {subjects.map((subject) => (
                    <label key={subject} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        value={subject}
                        {...register('subjects', { required: 'Please select at least one subject' })}
                        className="mr-2 w-4 h-4"
                      />
                      <span className="text-xs sm:text-sm">{subject}</span>
                    </label>
                  ))}
                </div>
                {errors.subjects && (
                  <p className="text-red-500 text-sm mt-1">{errors.subjects.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Classes You Can Teach *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {classes.map((cls) => (
                    <label key={cls} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        value={cls}
                        {...register('classes', { required: 'Please select at least one class' })}
                        className="mr-2 w-4 h-4"
                      />
                      <span className="text-xs sm:text-sm">{cls}</span>
                    </label>
                  ))}
                </div>
                {errors.classes && (
                  <p className="text-red-500 text-sm mt-1">{errors.classes.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability Options *
                </label>
                <div className="space-y-2">
                  {availabilityOptions.map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="checkbox"
                        value={option.value}
                        {...register('availability', { required: 'Please select at least one availability option' })}
                        className="mr-2"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.availability && (
                  <p className="text-red-500 text-sm mt-1">{errors.availability.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fees Per Hour (₹) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('feesPerHour', { 
                      required: 'Fees per hour is required',
                      min: { value: 0, message: 'Fees cannot be negative' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your hourly rate"
                  />
                  {errors.feesPerHour && (
                    <p className="text-red-500 text-sm mt-1">{errors.feesPerHour.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teaching Method
                  </label>
                  <select
                    {...register('teachingMethod')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select teaching method</option>
                    <option value="interactive">Interactive</option>
                    <option value="traditional">Traditional</option>
                    <option value="visual">Visual Learning</option>
                    <option value="practical">Practical Approach</option>
                    <option value="mixed">Mixed Approach</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio/About Yourself *
                </label>
                <textarea
                  {...register('bio', { required: 'Bio is required' })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us about yourself, your teaching philosophy, and what makes you unique..."
                />
                {errors.bio && (
                  <p className="text-red-500 text-sm mt-1">{errors.bio.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <FileText className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Document Upload
            </h3>
            
            <div className="space-y-4">
              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Photo *
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'photo')}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="flex items-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors text-sm sm:text-base"
                    >
                      <Camera className="mr-2 w-4 h-4" />
                      Upload Photo
                    </label>
                  </div>
                  {previewUrls.photo && (
                    <div className="relative">
                      <img
                        src={previewUrls.photo}
                        alt="Profile preview"
                        className="w-16 h-16 object-cover rounded-full border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile('photo')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Aadhar Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aadhar Card *
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'aadhar')}
                      className="hidden"
                      id="aadhar-upload"
                    />
                    <label
                      htmlFor="aadhar-upload"
                      className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 cursor-pointer transition-colors text-sm sm:text-base"
                    >
                      <Upload className="mr-2 w-4 h-4" />
                      Upload Aadhar
                    </label>
                  </div>
                  {uploadedFiles.aadhar && (
                    <div className="flex items-center text-green-600 flex-wrap gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs sm:text-sm break-all">{uploadedFiles.aadhar.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile('aadhar')}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* PAN Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN Card *
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'pan')}
                      className="hidden"
                      id="pan-upload"
                    />
                    <label
                      htmlFor="pan-upload"
                      className="flex items-center px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 cursor-pointer transition-colors text-sm sm:text-base"
                    >
                      <Upload className="mr-2 w-4 h-4" />
                      Upload PAN
                    </label>
                  </div>
                  {uploadedFiles.pan && (
                    <div className="flex items-center text-green-600 flex-wrap gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs sm:text-sm break-all">{uploadedFiles.pan.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile('pan')}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Referral Code Section */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-3 sm:p-4 md:p-6 rounded-lg border border-green-200 dark:border-green-800">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3 flex items-center">
              <Gift className="mr-2 w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
              Referral Code (Optional)
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              If you were referred by another teacher, enter their referral code here. This is optional.
            </p>
            <div>
              <input
                type="text"
                {...register('referralCode')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter referral code (e.g., ABC123)"
                maxLength={20}
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  // Auto-uppercase the input
                  const upperValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setValue('referralCode', upperValue);
                }}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave blank if you don't have a referral code
              </p>
            </div>
          </div>

          {/* Agreement Section */}
          <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg border-2 border-blue-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <FileText className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Teacher Registration Agreement
            </h3>
            
            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg border border-gray-300 max-h-64 sm:max-h-96 overflow-y-auto mb-4">
              <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-700">
                <div>
                  <h4 className="font-bold text-sm sm:text-base text-gray-900 mb-2">MAHSTRY – TEACHER REGISTRATION AGREEMENT</h4>
                  <p className="text-xs text-gray-600 mb-3 sm:mb-4">(To be accepted by ticking the checkbox at the time of registration)</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">1. Introduction</h5>
                  <p>By creating a teacher account on MahStry ("Platform"), you ("Teacher") agree to the terms and conditions mentioned in this Agreement.</p>
                  <p>MahStry provides tools for teachers to create profiles, manage batches, assign student work, and interact with students.</p>
                  <p>Ticking the checkbox indicates full acceptance of the terms below.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">2. Teacher Registration & Verification</h5>
                  <p>The Teacher must provide accurate, complete, and verifiable information.</p>
                  <p>MahStry reserves the right to verify documents and approve or reject the profile at its discretion.</p>
                  <p>Once approved, the Teacher will get access to the Teacher Dashboard.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">3. Commission Structure</h5>
                  <p>MahStry charges a 10% platform fee (inclusive of all applicable taxes including GST) from the teacher's earnings for each payment received through the platform.</p>
                  <p><strong>Example:</strong> If a student pays ₹2,000 to a teacher, MahStry deducts ₹200 (10% inclusive of all taxes) as commission, and the teacher receives ₹1,800.</p>
                  <p>The platform fee is deducted from the teacher's earnings, not charged to the student.</p>
                  <p>Teachers are free to negotiate fees directly with students.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">4. Batch & Feature Limits</h5>
                  <p>Each teacher will receive 3 active batches for free.</p>
                  <p>If the Teacher wants to add additional batches, extra charges may apply.</p>
                  <p>MahStry may also offer additional premium features (analytics, automation, special tools, early access features, etc.) that may be priced separately.</p>
                  <p>Charges for such additional features will be communicated inside the platform before activation.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">5. Profile Permanence & Deletion Policy</h5>
                  <p>Once the Teacher profile is created and verified, it cannot be deleted directly from the app.</p>
                  <p>If the Teacher wishes to delete their account, they must send an email to: <a href="mailto:support@mahstry.com" className="text-blue-600 hover:underline">support@mahstry.com</a></p>
                  <p>The deletion request will undergo internal review and may take up to 7–14 business days.</p>
                  <p>MahStry may retain necessary data as required for:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>compliance with law</li>
                    <li>fraud prevention</li>
                    <li>transaction record keeping</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">6. Teacher Responsibilities</h5>
                  <p>By registering, the Teacher agrees to:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Maintain professional behavior with all students.</li>
                    <li>Promote scientific temper—evidence-based teaching without superstition or misinformation—and ensure zero discrimination, bias, hate speech, or harassment toward any learner.</li>
                    <li>Avoid any abusive, illegal, or unethical conduct.</li>
                    <li>Deliver promised classes, assignments, and support.</li>
                    <li>Not misuse student contact details for spam or unauthorized promotion.</li>
                    <li>Not transfer or sell their teacher account to someone else.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">7. Platform Responsibilities</h5>
                  <p>MahStry will:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Provide the dashboard, tools, and basic support.</li>
                    <li>Connect students who discover the Teacher via platform search.</li>
                    <li>Deduct only the agreed commission from the first payment.</li>
                  </ul>
                  <p>MahStry does not guarantee student acquisition, minimum income, or retention.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">8. Payments & Settlements</h5>
                  <p>All payments processed through MahStry will be settled to the Teacher's registered bank account.</p>
                  <p>Settlement timelines depend on bank processing and payment gateways.</p>
                  <p>Any disputes must be reported via email within 7 days.</p>
                  <p className="mt-2"><strong>KYC Verification:</strong> For future payment processing and compliance with Razorpay's verification requirements, MahStry may request additional KYC (Know Your Customer) documents from teachers. This may include bank account verification, additional identity documents, or other information as required by payment gateways and regulatory authorities. Teachers agree to provide such documentation when requested to ensure smooth payment settlements.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">9. Termination & Suspension</h5>
                  <p>MahStry may suspend or remove a Teacher's account if:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Fraudulent documents are submitted</li>
                    <li>Repeated student complaints occur</li>
                    <li>Terms of this agreement are violated</li>
                    <li>Any harmful activity or misconduct is detected</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">10. Changes to Agreement</h5>
                  <p>MahStry reserves the right to modify this Agreement at any time.</p>
                  <p>Updated terms will be shown to the Teacher inside the app for approval.</p>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">11. Acceptance</h5>
                  <p>By ticking the checkbox, you confirm:</p>
                </div>
              </div>
            </div>

            {/* Agreement Checkboxes */}
            <div className="space-y-3 mb-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreementAccepted.readAndUnderstood}
                  onChange={(e) => setAgreementAccepted(prev => ({ ...prev, readAndUnderstood: e.target.checked }))}
                  className="mt-1 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  I have read and understood the MahStry Teacher Agreement.
                </span>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreementAccepted.agreeToTerms}
                  onChange={(e) => setAgreementAccepted(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                  className="mt-1 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  I agree to all terms, including commission, batch limits, deletion procedure, and charges.
                </span>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreementAccepted.consentToData}
                  onChange={(e) => setAgreementAccepted(prev => ({ ...prev, consentToData: e.target.checked }))}
                  className="mt-1 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  I consent to the use of my data for verification and operational purposes.
                </span>
              </label>
            </div>

            <div className="text-xs sm:text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
              <p>For any queries, please contact us at: <a href="mailto:support@mahstry.com" className="text-blue-600 hover:underline font-medium break-all">support@mahstry.com</a></p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4 sm:gap-0 pt-4 sm:pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !agreementAccepted.readAndUnderstood || !agreementAccepted.agreeToTerms || !agreementAccepted.consentToData}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-sm sm:text-base"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Submit Registration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TuitionTeacherRegistration;
